import { CheckCircle2, ExternalLink, FileJson, Gauge, Lock, PhoneOutgoing, RefreshCw, ShieldAlert, Smartphone, Unlock } from "lucide-react";
import { cn } from "@wa/ui";
import {
  formatBytes,
  formatMs,
  scoreBand,
  sortFindings,
  type SiteScan,
} from "../../../lib/siteScan";
import { Btn, StatusPill } from "../../ui";
import { useAiTestCall } from "../../telephony/aiTestCall";
import { ScoreDial } from "./ScoreDial";
import { SEVERITY_COLOUR } from "./severity";
import type { T } from "./types";

/**
 * The verdict, above the fold: what the site runs on, how attractive a lead it
 * is, and the three sentences worth opening a call with.
 *
 * Everything below this card is evidence. If an operator reads only this card,
 * they should already know whether to pick up the phone.
 */

/**
 * The host the operator's domain sent us to, when that is somewhere else.
 *
 * A bare `www.` difference is not worth a line - almost every site has one. A
 * different host is: the card is filed under the domain that was asked for
 * while every number on it describes the site that domain forwards to, and
 * without saying so the report looks like it is about the wrong company.
 */
function movedTo(requestedUrl: string, finalUrl: string): string | null {
  try {
    const bare = (u: string) => new URL(u).hostname.replace(/^www\./i, "");
    return bare(requestedUrl) === bare(finalUrl) ? null : new URL(requestedUrl).hostname;
  } catch {
    return null;
  }
}

/** Platform tile: the initial in a coloured square, since we ship no vendor logos. */
function PlatformMark({ name, custom }: { name: string; custom: boolean }) {
  return (
    <span
      className={cn(
        "flex size-11 shrink-0 items-center justify-center rounded-2xl text-lg font-semibold",
        custom ? "bg-[var(--crm-sunk)] text-muted-foreground" : "bg-primary/10 text-primary",
      )}
      aria-hidden
    >
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

/**
 * One transport fact.
 *
 * The good and bad shades come from the theme's success and destructive colours
 * rather than a hand-picked emerald/rose pair: "no TLS" is the same verdict a
 * late status pill gives, and the operator should not have to learn two
 * vocabularies for one idea.
 */
function Fact({
  icon: Icon,
  label,
  value,
  tone = "neutral",
}: {
  icon: typeof Gauge;
  label: string;
  value: string;
  tone?: "neutral" | "good" | "bad";
}) {
  const colour =
    tone === "good" ? "var(--color-success)" : tone === "bad" ? "var(--color-destructive)" : undefined;
  return (
    <div className="rounded-2xl bg-[var(--crm-sunk)] px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <Icon className="size-3.5" style={{ color: colour }} />
        {label}
      </div>
      <p className="mt-1 truncate text-sm font-semibold" style={{ color: colour }} title={value}>
        {value}
      </p>
    </div>
  );
}

export function ScanHero({
  scan,
  onRefresh,
  onRaw,
  refreshing,
  t,
}: {
  scan: SiteScan;
  onRefresh: () => void;
  /** Absent inside a dialog, where a second modal would stack on the first. */
  onRaw?: () => void;
  refreshing: boolean;
  t: T;
}) {
  const aiCall = useAiTestCall();
  const { platform, http } = scan;
  const primary = platform.cms ?? platform.ecommerce;
  const band = scoreBand(scan.opportunityScore);

  // The pitch: the worst three findings, which is what a call opens with.
  const pitch = sortFindings(scan.findings)
    .filter((f) => f.severity === "critical" || f.severity === "warning")
    .slice(0, 3);

  return (
    <section className="crm-card overflow-hidden">
      {/* 24px down each side is a desk figure. Inside the leads dialog on a
          360px screen the hero is already the third box in from the viewport,
          and that padding was the last 48px the pitch had to lose. */}
      <div className="grid gap-5 p-4 sm:gap-6 sm:p-6 lg:grid-cols-[1fr_auto] lg:gap-8">
        <div className="min-w-0">
          {/* Identity */}
          <div className="flex items-start gap-3">
            <PlatformMark name={primary?.name ?? scan.domain} custom={!primary} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-lg font-bold tracking-[-0.02em]">
                  {primary ? primary.name : t("scanner.platform.custom")}
                </h2>
                {primary?.version && <StatusPill tone="info">{primary.version}</StatusPill>}
                {primary && (
                  <span className="text-[11px] text-muted-foreground">
                    {t("scanner.confidence", { value: primary.confidence })}
                  </span>
                )}
                {scan.cached && <StatusPill tone="mut">{t("scanner.cached")}</StatusPill>}
                {/* What the site is FOR, next to what it is built with. The two
                    together are the whole verdict: an old WooCommerce shop and
                    an old one-page portfolio are not the same phone call. */}
                {scan.profile && scan.profile.kind !== "unknown" && (
                  <StatusPill tone="info">{t(`scanner.kind.${scan.profile.kind}`)}</StatusPill>
                )}
              </div>

              <a
                href={http.finalUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-0.5 inline-flex max-w-full items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary"
              >
                {/* `min-w-0`, or the ellipsis never appears. The span is a flex
                    item, a flex item refuses to shrink below its own min-content
                    by default, and a URL has nothing to break at - so the whole
                    address was drawn past the edge of the card instead of being
                    cut at it. `truncate` on the anchor cannot help: it is the
                    flex container, and text-overflow does not draw on one. */}
                <span className="min-w-0 truncate">{http.finalUrl}</span>
                <ExternalLink className="size-3 shrink-0" />
              </a>

              {movedTo(http.requestedUrl, http.finalUrl) && (
                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                  {t("scanner.redirectedFrom", { host: movedTo(http.requestedUrl, http.finalUrl) ?? "" })}
                </p>
              )}

              {platform.ecommerce && platform.cms && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("scanner.alsoShop", {
                    name: platform.ecommerce.name,
                    version: platform.ecommerce.version ?? "",
                  })}
                </p>
              )}
            </div>
          </div>

          {/* Transport facts */}
          <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Fact
              icon={http.https ? Lock : Unlock}
              label={t("scanner.fact.tls")}
              value={t(http.https ? "scanner.fact.tlsOn" : "scanner.fact.tlsOff")}
              tone={http.https ? "good" : "bad"}
            />
            <Fact
              icon={Smartphone}
              label={t("scanner.fact.mobile")}
              value={t(scan.seo?.viewport ? "scanner.fact.mobileOn" : "scanner.fact.mobileOff")}
              tone={scan.seo?.viewport ? "good" : "bad"}
            />
            <Fact
              icon={Gauge}
              label={t("scanner.fact.speed")}
              value={formatMs(http.ttfbMs)}
              tone={http.ttfbMs != null && http.ttfbMs > 1200 ? "bad" : "neutral"}
            />
            <Fact icon={FileJson} label={t("scanner.fact.size")} value={formatBytes(http.htmlBytes)} />
          </div>

          {/* Pitch */}
          {pitch.length > 0 && (
            <div className="mt-5">
              {/* The design's column-head treatment, used here as a section
                  label: 10.5px at 800 with wide tracking. */}
              <p className="text-[10.5px] font-extrabold uppercase tracking-[0.13em] text-muted-foreground">
                {t("scanner.pitch.title")}
              </p>
              <ul className="mt-2 space-y-1.5">
                {pitch.map((f) => (
                  <li key={f.id} className="flex items-start gap-2 text-sm">
                    <ShieldAlert
                      className="mt-0.5 size-4 shrink-0"
                      style={{ color: SEVERITY_COLOUR[f.severity] }}
                    />
                    <span>{t(`scanner.finding.${f.id}`, f.vars)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {pitch.length === 0 && (
            <p className="mt-5 flex items-center gap-2 text-sm" style={{ color: "var(--color-success)" }}>
              <CheckCircle2 className="size-4" /> {t("scanner.pitch.none")}
            </p>
          )}
        </div>

        {/* Score */}
        {/* Wraps below `sm`. The dial is a fixed 132px and the two buttons want
            about 150 more, which is more than a 360px screen has left once the
            dialog, the card and this padding have taken their share - so the
            buttons were drawn off the right edge of the card. Wrapped they sit
            under the dial, and both halves centre so the pair still reads as
            one block rather than as a stranded row. */}
        <div className="flex flex-wrap items-center justify-center gap-4 border-t border-[var(--crm-hair)] pt-5 sm:flex-nowrap sm:justify-between lg:flex-col lg:justify-center lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0">
          <div className="flex flex-col items-center gap-1.5">
            <ScoreDial
              score={scan.opportunityScore}
              label={t("scanner.score.label")}
              caption={t(`scanner.score.${band}`)}
            />
            {/* Without this line the dial is a number that disagrees with the
                findings below it. Only shown when the profile actually moved
                it, so a shop's score carries no unnecessary footnote. */}
            {scan.profile && scan.defectScore !== scan.opportunityScore && (
              <p className="max-w-[9.5rem] text-center text-[11px] leading-snug text-muted-foreground">
                {t("scanner.score.weighted", {
                  defect: scan.defectScore,
                  factor: scan.profile.factor.toFixed(2),
                })}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Btn small onClick={onRefresh} disabled={refreshing}>
              <RefreshCw className={cn("size-3.5", refreshing && "animate-spin")} />
              {t("scanner.action.rescan")}
            </Btn>
            {onRaw ? (
              <Btn small onClick={onRaw}>
                <FileJson className="size-3.5" />
                {t("scanner.action.raw")}
              </Btn>
            ) : null}
            {aiCall.canCall ? (
              <Btn
                small
                onClick={() =>
                  aiCall.open({
                    domain: scan.domain,
                    regCode: scan.identity?.register?.regCode ?? null,
                    name: scan.identity?.companyName ?? null,
                  })
                }
              >
                <PhoneOutgoing className="size-3.5" />
                {t("aiOutbound.testCall.button")}
              </Btn>
            ) : null}
          </div>
        </div>
      </div>

      {scan.error && (
        <p
          className="px-6 py-2.5 text-xs"
          style={{
            background: "color-mix(in oklab, var(--color-warning) 13%, transparent)",
            color: "color-mix(in oklab, var(--color-warning) 52%, var(--color-foreground))",
          }}
        >
          {t("scanner.partial", { detail: scan.error })}
        </p>
      )}
    </section>
  );
}
